import { useState, useEffect } from 'react';

const KNOWLEDGEBASE_URL = 'https://takedownleague.tawk.help/category/getting-started';

interface HelpSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
}

export function HelpSlideout({ isOpen, onClose }: HelpSlideoutProps) {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setIframeLoaded(false);
      setIframeError(false);
    }
  }, [isOpen]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Slideout - Account for bottom nav (64px) */}
      <div
        className={`fixed top-0 right-0 bottom-16 w-full max-w-md bg-[#1e1e1e] z-50 shadow-2xl transform transition-transform duration-300 flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="bg-[#2c2c2c] border-b border-gray-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-200 transition-colors"
              aria-label="Close help"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h2 className="text-lg font-bold text-white">Help Center</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 transition-colors text-xl"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 relative bg-white">
          {!iframeLoaded && !iframeError && (
            <div className="absolute inset-0 flex justify-center items-center bg-[#1e1e1e]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-400">Loading help center...</p>
              </div>
            </div>
          )}

          {iframeError ? (
            <div className="absolute inset-0 flex justify-center items-center bg-[#1e1e1e]">
              <div className="text-center px-6">
                <p className="text-4xl mb-3">📖</p>
                <p className="text-white font-medium mb-2">Unable to load help center</p>
                <button
                  onClick={() => window.open(KNOWLEDGEBASE_URL, '_blank')}
                  className="mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm transition-colors"
                >
                  Open in new tab
                </button>
              </div>
            </div>
          ) : (
            <iframe
              src={KNOWLEDGEBASE_URL}
              title="Help Center"
              className="w-full h-full border-0"
              onLoad={() => setIframeLoaded(true)}
              onError={() => setIframeError(true)}
            />
          )}
        </div>
      </div>
    </>
  );
}
